import { Children, useDeferredValue, useEffect, useState } from "react";
import { AddIcon, CheckListIcon } from "allIcons";
import {
  BodyContainer,
  FiltersList,
  HeaderContainer,
  InputField,
  ListCard,
  ListIconWrapper,
  Loader,
  LoadingListCards,
  MainButton,
  PrimaryHeading,
  RightMainContainer,
  TemplateNotFound,
} from "commonUI";
import {
  useCheckListTemplates,
  useAddChecklistTemplate,
  useDeleteChecklistTemplate,
} from "api/templateLibrary/checklists";
import { Link } from "react-router-dom";
import { isEqual, omit, parse, search } from "helpers";
import { useUserPermissions, useUserRole } from "hooks/user";
import {
  libraryItemFiltering,
  libraryItemSorting,
} from "filterAndSort/templateLibrary";
import { useTags } from "api/siteSettings/Tags";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function Checklist() {
  const { data: checklists, isLoading } = useCheckListTemplates();
  const { data: tags } = useTags();
  const { mutateAsync: addChecklist, isLoading: addingChecklist } =
    useAddChecklistTemplate();
  const { mutateAsync: deleteChecklist, isLoading: deletingChecklist } =
    useDeleteChecklistTemplate();
  const { add_new_templates, edit_templates, delete_templates } =
    useUserPermissions();
  const [searchText, setSearchText] = useState("");
  const defferedSearch = useDeferredValue(searchText);
  const [searchResult, setSearchResult] = useState([]);
  const [filterData, setFilterData] = useState({});
  const { isSuperAdmin } = useUserRole();

  useEffect(() => {
    setSearchResult(
      checklists.filter((task) => search(task.title, defferedSearch))
    );
  }, [defferedSearch, checklists]);

  const applySorting = (order, orderBy) => {
    setSearchResult((prevSearchResult) =>
      libraryItemSorting(order, orderBy, prevSearchResult)
    );
  };

  const applyFilter = ({
    selectedFilter,
    selectedTags,
    selectedLibrarySource,
    ...data
  }) => {
    setFilterData({
      ...data,
      selectedTags,
      selectedLibrarySource,
      selectedFilter,
    });
    setSearchResult(
      libraryItemFiltering(
        selectedFilter,
        selectedTags,
        selectedLibrarySource,
        checklists
      )
    );
  };

  return (
    <>
      <RightMainContainer>
        <Loader show={addingChecklist || deletingChecklist} />
        <HeaderContainer>
          <PrimaryHeading>
            {t("libraryTemplate.checklist.title")}
          </PrimaryHeading>
          {add_new_templates ? (
            <Link to={`/${ModulePrefixes.TEMPLATE_LIBRARY}/checklists/add`}>
              <MainButton>
                <AddIcon />
                {t("button.add")}
              </MainButton>
            </Link>
          ) : (
            ""
          )}
        </HeaderContainer>
        <BodyContainer>
          <InputField
            placeholder={t("input.search.placeholder")}
            searchIcon={true}
            filter={true}
            value={searchText}
            setValue={(val) => setSearchText(val)}
            tags={tags ?? []}
            onApplyFilter={applyFilter}
            applySorting={applySorting}
            filterData={filterData}
          />
          <FiltersList filterData={filterData} updateFilter={applyFilter} />
          {isLoading ? (
            <LoadingListCards />
          ) : searchResult.length === 0 ? (
            <TemplateNotFound
              label={t("libraryTemplate.checklist.title")}
              link={add_new_templates ? `/${ModulePrefixes.TEMPLATE_LIBRARY}/checklists/add` : ""}
              linkLabel={t("libraryTemplate.checklist.title")}
            />
          ) : (
            Children.toArray(
              searchResult.map((checklist) => {
                return (
                  <ListCard
                    data={checklist.title}
                    duplicate={add_new_templates}
                    editIcon={edit_templates}
                    deleteIcon={
                      delete_templates
                        ? isEqual(checklist.system_status, 1) && !isSuperAdmin
                        : true
                    }
                    secondaryData={
                      isEqual(checklist.system_status, 1) && !isSuperAdmin
                        ? t("module.sourceType.system")
                        : ""
                    }
                    link={
                      edit_templates
                        ? `/${ModulePrefixes.TEMPLATE_LIBRARY}/checklists/${checklist.id}`
                        : ""
                    }
                    strictLink={true}
                    onDuplicate={() => {
                      addChecklist(
                        omit(["id"], {
                          ...checklist,
                          title: `Copy Of ${checklist.title}`,
                          tasks: parse(checklist.tasks),
                          tags: parse(checklist.tags),
                          priority: parse(checklist.priority),
                          system_status: isSuperAdmin ? "1" : "0",
                        })
                      );
                    }}
                    onDelete={() => {
                      if (!delete_templates) return;
                      if (isEqual(checklist.system_status, 1) && !isSuperAdmin)
                        return;
                      deleteChecklist(checklist.id);
                    }}
                    icon={
                      <ListIconWrapper>
                        <CheckListIcon fill="white" />
                      </ListIconWrapper>
                    }
                    deleteTitle={t("libraryTemplate.delete.title")}
                    deleteButtonText={t("libraryTemplate.delete.button")}
                    deleteMessage={t("libraryTemplate.delete.message")}
                  />
                );
              })
            )
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
